/**
 * @summary - Component that Provides Asset Category Listing Page
 * @author Yuvasri S
 * @since 2024-05-28
 */
import React, { useState, useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import CustomDialog from "../commoncomponents/CustomDialog";
import { useNavigate } from "react-router-dom";
import { DefaultCard } from "../../assets/styled-components/card";
import {
  getAuthToken,
  getFacilityId,
  getcompanyId,
} from "../../utility/tokenUtility";
import axios from "axios";
import CreateNewButton from "../commoncomponents/CreateNewButton";
import RefreshExportButtons from "../commoncomponents/RefreshExportButtons";
import { MasterGraph } from "../../utility/apiURLS";
import CloseButton from "../commoncomponents/CloseButton";
import PageTitle from "../commoncomponents/PageTitle";
import SectionTitle from "../commoncomponents/SectionTitle";
import utilities from "../../utility/utilities";
import { useAccess } from "../../AccessContext";

import CustomSelectInput from "../commoncomponents/CustomSelectInput";

const columns = [
  { field: "assetcategory", headerName: "Asset Type Code Group", width: 180 },
  {
    field: "assetcategorydesc",
    headerName: "Asset Type Code Group Description",
    width: 150,
  },
];

const AssetCategoryList = () => {
  const [filteredRows, setFilteredRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [deletedRowDetails, setDeletedRowDetails] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const [gridApi, setGridApi] = useState(null);
  const [filterModel, setFilterModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportCount, setExportCount] = useState(0)

  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "ENGM018");
  const serviceOptions = [
    { label: "FEMS", value: "FEMS", id: 1 },
    { label: "BEMS", value: "BEMS", id: 2 }
  ];
  const [service, setService] = useState(serviceOptions[0]);
  const [isLoading, setIsLoading] = useState(false);

  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const companyId = getcompanyId("companyId");

  const handleServiceChange = (selectedService) => {
    setService(selectedService);
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([]);
  };

  const fetchAssetCategories = async () => {
    setIsLoading(true);
    setFilteredRows([])
    try {
      const response = await axios.get(
        `${MasterGraph}/api/graph/GetAssetCategoryByCompanyId/${companyId},${service.value}?page=${pageIndex}&size=${pageSize}&filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`,
        config
      );
      setFilteredRows(response.data.items);
      setRowCount(response.data.totalCount);
      setExportCount(response.data.totalCount)
    } catch (error) {
      console.error("Error fetching asset categories:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExportdata = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${MasterGraph}/api/graph/GetExportCategory/${companyId},${service.value}?filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`,
        config
      );
      setExportData(response.data);
    } catch (error) {
      console.error("Error fetching asset categories:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteAssetCategory = async (id) => {
    try {
      await axios.delete(`${MasterGraph}/api/masassetcategories/${id}`, config);
      fetchAssetCategories();
      return true;
    } catch (error) {
      console.error("Error deleting asset category:", error);
      if (error.response && error.response.status === 400) {
        setErrorMessage(
          "Record cannot be deleted as it is referred in another record."
        );
      } else {
        setErrorMessage(
          "An error occurred while deleting the Asset category. Please try again."
        );
      }
      return false;
    }
  };

  useEffect(() => {
    fetchAssetCategories();
  }, [filterModel, sortModel, pageIndex, pageSize]);

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (rowToDelete) {
      const isDeleted = await deleteAssetCategory(rowToDelete.id);
      setDeleteDialogOpen(false);
      if (isDeleted) {
        setDeletedRowDetails({
          code: rowToDelete.assetcategory,
          desc: rowToDelete.assetcategorydesc,
        });
        setConfirmationDialogOpen(true);
      }
      setRowToDelete(null);
    }
  };

  const handleCreateWorkGroup = () => {
    navigate("register");
  };

  const handleEdit = (rowData) => {
    navigate("register", {
      state: { ...rowData, readOnlyEdit: true, mode: "edit" },
    });
  };

  const handleView = (rowData) => {
    navigate("register", {
      state: { ...rowData, readOnly: true, mode: "view" },
    });
  };

  const handleRefresh = () => {
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([])
  };

  return (
    <>
      <PageTitle title="Asset Type Code Group" />
      <DefaultCard>
        <Box
          mb={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <SectionTitle title="Asset Type Code Group List" />
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <CustomSelectInput
              fullWidth={false}
              labelSize={"14px"}
              sx={{ width: 150 }}
              label={"Service"}
              options={serviceOptions}
              value={service}
              onChange={handleServiceChange}
              placeholder={"Select Service"}
              highlight={true}
            />
            {access?.canCreate && <CreateNewButton onClick={handleCreateWorkGroup} />}
            <RefreshExportButtons
              exportFunction={fetchExportdata}
              exportData={exportData}
              refData={gridApi}
              handleRefresh={handleRefresh}
              title="Asset Category"
            />
          </Stack>
        </Box>
        <CustomAgGrid
          rows={filteredRows}
          columns={columns}
          handleEdit={access?.canEdit && handleEdit}
          handleDelete={access?.canDelete && handleDelete}
          handleView={access?.canView && handleView}
          setGridApi={setGridApi}
          pagination={false}
          isLoading={isLoading}
          setFilterModel={setFilterModel}
          setSortModel={setSortModel}
          totalCount={rowCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
        />

      </DefaultCard>
      <CloseButton />
      <CustomDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={confirmDelete}
        title="Confirmation!"
        content="Are you sure you want to delete?"
        cancelBtn="No"
        confirmBtn="Yes"
      />
      <CustomDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        title="Deletion Successful"
        content="Record deleted successfully."
        confirmBtn="OK"
        onConfirm={() => setConfirmationDialogOpen(false)}
      />
      <CustomDialog
        open={!!errorMessage}
        onClose={() => setErrorMessage("")}
        onConfirm={() => setErrorMessage("")}
        content={errorMessage}
        confirmBtn="Ok"
      />
    </>
  );
};

export default AssetCategoryList;
